import React, { useCallback, useContext, useMemo } from "react"
import { FieldNames, formValidate } from "./constants"
import { QueryContext } from "../../../../contexts"
import { updateConnection } from "../../configurations/domain"
import { ConnectionForm } from "./form"
import { useBalkError } from "../../../../hooks/useBalkError"

export const EditConnectionForm = ({
  connections,
  schedule,
  onCancel,
  onSave,
  rowsClassName,
  buttonsClassName,
  children,
}) => {
  const { getHookState } = useContext(QueryContext)
  const { request, loading } = getHookState(updateConnection)
  const handler = useBalkError({
    onUpdate: onSave,
    successMessage:
      "Successfully updated. <i><strong>Changes</strong> may take up to 1 minute to take effect in our system.</i>",
    buildErrorMessage: error => {
      const record = connections.find(connection => error.record.id === connection.id)
      if (!record) return
      return `Connection for ${record.tutor.first_name} ${record.tutor.last_name} hasn't been updated`
    },
  })

  const multipleValues = useMemo(() => {
    const values = {
      [FieldNames.StartDate]: false,
      [FieldNames.EndDate]: false,
      [FieldNames.MaxPaidNoShows]: false,
      [FieldNames.Rate]: false,
      [FieldNames.Position]: false,
    }
    if (connections.length === 1) return values
    connections.reduce((con1, con2) => {
      if (!values[FieldNames.StartDate]) values[FieldNames.StartDate] = con1.start_on !== con2.start_on
      if (!values[FieldNames.EndDate]) values[FieldNames.EndDate] = con1.end_on !== con2.end_on
      if (!values[FieldNames.MaxPaidNoShows])
        values[FieldNames.MaxPaidNoShows] = con1.max_paid_noshows !== con2.max_paid_noshows
      if (!values[FieldNames.Rate]) values[FieldNames.Rate] = con1.rate !== con2.rate
      if (!values[FieldNames.Position]) values[FieldNames.Position] = con1.position_level !== con2.position_level
      return con2
    })
    return values
  }, [connections])

  const initialValues = useMemo(() => {
    const [connection] = connections
    return {
      [FieldNames.StartDate]: multipleValues[FieldNames.StartDate] ? null : connection.start_on,
      [FieldNames.EndDate]: multipleValues[FieldNames.EndDate] ? null : connection.end_on,
      [FieldNames.MaxPaidNoShows]: multipleValues[FieldNames.MaxPaidNoShows] ? null : connection.max_paid_noshows,
      [FieldNames.Rate]: multipleValues[FieldNames.Rate] ? null : connection.rate,
      [FieldNames.Position]: multipleValues[FieldNames.Position] ? null : connection.position_level,
    }
  }, [connections, multipleValues])

  const onSubmit = useCallback(
    values => {
      if (!connections?.length) {
        return
      }

      // Single connection update
      // controllers/api/schedules/connections_controller.rb#update
      if (connections.length === 1 && schedule) {
        const connectionId = connections[0].id
        const scheduleId = schedule.id

        if (!connectionId || !scheduleId) return

        request({
          url: `/api/schedules/${scheduleId}/connections/${connectionId}`,
          urlBuilderConfig: null,
          data: {
            connection: values,
          },
          onSuccess: handler,
        })

        return
      }

      // Bulk update
      // controllers/api/connections_controller.rb#update
      const filteredFormValues = Object.entries(values).reduce((fieldsToUpdate, [key, value]) => {
        // Only exclude null values, keep empty stringssssss
        if (value !== null) {
          fieldsToUpdate[key] = value
        }

        return fieldsToUpdate
      }, {})

      request({
        url: "/api/connections",
        method: "PATCH",
        urlBuilderConfig: null,
        data: {
          ids: connections.map(({ id }) => id),
          ...filteredFormValues,
        },
        onSuccess: handler,
      })
    },
    [connections, handler, request, schedule]
  )

  return (
    <ConnectionForm
      multipleValues={multipleValues}
      initialValues={initialValues}
      validate={formValidate({ checkPositionField: connections.length === 1, multipleValues })}
      showPositionField={connections.length === 1}
      loading={loading}
      rowsClassName={rowsClassName}
      buttonsClassName={buttonsClassName}
      onSubmit={onSubmit}
      onCancel={onCancel}
    >
      {children}
    </ConnectionForm>
  )
}
